import React from 'react';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { Loader2 } from 'lucide-react';

export interface SpinnerProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Size of the spinner */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Color variant */
  variant?: 'primary' | 'white' | 'gray';
}

const spinnerSizes = {
  sm: 'h-4 w-4',
  md: 'h-6 w-6',
  lg: 'h-8 w-8',
  xl: 'h-12 w-12',
};

const spinnerColors = {
  primary: 'text-indigo-600',
  white: 'text-white',
  gray: 'text-gray-400',
};

/**
 * Spinner loading indicator
 */
export const Spinner: React.FC<SpinnerProps> = ({
  className,
  size = 'md',
  variant = 'primary',
  ...props
}) => {
  return (
    <div className={twMerge(clsx('flex items-center justify-center', className))} {...props}>
      <Loader2
        className={clsx(
          'animate-spin',
          spinnerSizes[size],
          spinnerColors[variant]
        )}
      />
    </div>
  );
};

export interface LoadingOverlayProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Show the overlay */
  show?: boolean;
  /** Loading message */
  message?: string;
}

/**
 * Full-screen loading overlay
 */
export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({
  className,
  show = true,
  message = 'Loading...',
  ...props
}) => {
  if (!show) return null;

  return (
    <div
      className={twMerge(
        clsx(
          'fixed inset-0 z-50 flex flex-col items-center justify-center',
          'bg-white/80 backdrop-blur-sm',
          className
        )
      )}
      {...props}
    >
      <Spinner size="xl" />
      {message && <p className="mt-4 text-gray-600 font-medium">{message}</p>}
    </div>
  );
};

export interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Width of skeleton */
  width?: string | number;
  /** Height of skeleton */
  height?: string | number;
  /** Make skeleton circular */
  circle?: boolean;
}

/**
 * Skeleton loading placeholder
 */
export const Skeleton: React.FC<SkeletonProps> = ({
  className,
  width,
  height,
  circle,
  style,
  ...props
}) => {
  return (
    <div
      className={twMerge(
        clsx(
          'bg-gray-200 animate-pulse',
          circle ? 'rounded-full' : 'rounded-md',
          className
        )
      )}
      style={{
        width: typeof width === 'number' ? `${width}px` : width,
        height: typeof height === 'number' ? `${height}px` : height,
        ...style,
      }}
      {...props}
    />
  );
};

/**
 * Loading page component
 */
export const LoadingPage: React.FC<{ message?: string }> = ({ message }) => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <Spinner size="xl" />
        {message && <p className="mt-4 text-gray-600">{message}</p>}
      </div>
    </div>
  );
};

export default Spinner;
